import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { GridColDef, GridRowModel } from "@mui/x-data-grid";

import {
  fetchDeleteTeachers,
  fetchDeleteTeachersAll,
  fetchGetTeachersAll,
  fetchPostTeachers,
  fetchPutTeacher,
  PSDataGrid,
  SetSnackType,
  SettingsGrades,
  TEACHER_PREFIXES,
  TeachersNoNulls,
  Upload,
  UploadOverwriteDialog,
} from "../../utils";

const DoDelete = () => {
  return useCallback(
    (tch: TeachersNoNulls) =>
      new Promise<TeachersNoNulls>(async (resolve, reject) => {
        try {
          const result = (await fetchDeleteTeachers([tch])) as TeachersNoNulls;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPost = () => {
  return useCallback(
    (tch: TeachersNoNulls) =>
      new Promise<TeachersNoNulls>(async (resolve, reject) => {
        try {
          const result = (await fetchPostTeachers([tch])) as TeachersNoNulls;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPut = () => {
  return useCallback(
    (tch: TeachersNoNulls) =>
      new Promise<any>(async (resolve, reject) => {
        if (tch.prefix?.trim() === "" || tch.last?.trim() === "") {
          reject(new Error("Error while saving: prefix or last can't be empty."));
        } else {
          try {
            const updatedDoc = (await fetchPutTeacher(tch)) as TeachersNoNulls;
            resolve(updatedDoc);
          } catch (err: any) {
            reject(err);
          }
        }
      }),
    [],
  );
};

export const Teachers = (props: {
  disconnect: () => void;
  setSnack: SetSnackType;
  setTeachers: Dispatch<SetStateAction<TeachersNoNulls[]>>;
  settingsGrades: SettingsGrades;
  teachers: TeachersNoNulls[];
}) => {
  const [rowsLoading, setRowsLoading] = useState(false);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogCancelOpen, setAddDialogCancelOpen] = useState(false);
  const [addTeacherPrefix, setAddTeacherPrefix] = useState("N/A");
  const [addTeacherFirst, setAddTeacherFirst] = useState(null as string | null);
  const [addTeacherLast, setAddTeacherLast] = useState(null as string | null);
  const [addTeacherYears, setAddTeacherYears] = useState(0);
  // const [addTeacherGrades, setAddTeacherGrades] = useState(Array<boolean>()); todo: `grades` array
  const [addTeacherGrade0am, setAddTeacherGrade0am] = useState(false);
  const [addTeacherGrade0pm, setAddTeacherGrade0pm] = useState(false);
  const [addTeacherGrade1, setAddTeacherGrade1] = useState(false);
  const [addTeacherGrade2, setAddTeacherGrade2] = useState(false);
  const [addTeacherGrade3, setAddTeacherGrade3] = useState(false);
  const [addTeacherGrade4, setAddTeacherGrade4] = useState(false);
  const [addTeacherGrade5, setAddTeacherGrade5] = useState(false);
  const [addTeacherGrade6, setAddTeacherGrade6] = useState(false);
  const [addTeacherGrade7, setAddTeacherGrade7] = useState(false);
  const [addTeacherGrade8, setAddTeacherGrade8] = useState(false);
  const [addTeacherGrade9, setAddTeacherGrade9] = useState(false);
  const [addTeacherGrade10, setAddTeacherGrade10] = useState(false);
  const [addTeacherGrade11, setAddTeacherGrade11] = useState(false);
  const [addTeacherGrade12, setAddTeacherGrade12] = useState(false);
  const [addTeacherGrade13, setAddTeacherGrade13] = useState(false);
  const [addTeacherFirstError, setAddTeacherFirstError] = useState(false);
  const [addTeacherLastError, setAddTeacherLastError] = useState(false);
  const [addTeacherYearsError, setAddTeacherYearsError] = useState(false);

  const [actionDeleteVisible, setActionDeleteVisible] = useState(false);

  const [uploadPendingRows, setUploadPendingRows] = useState(null as Upload[] | null);
  const [openUploadOverwrite, setOpenUploadOverwrite] = useState(false);

  const addRow = DoPost();
  const removeRow = DoDelete();
  const mutateRow = DoPut();

  useEffect(() => {
    (async () => {
      setRowsLoading(true);
      const result = await fetchGetTeachersAll();
      if (Object.keys(result).includes("error")) {
        props.disconnect();
        props.setSnack((result as any).error?.toString(), "error");
      } else {
        props.setTeachers(result as TeachersNoNulls[]);
      }
      setRowsLoading(false);
    })();
  }, []);

  const resetAddTeacher = () => {
    setAddTeacherPrefix("N/A");
    setAddTeacherFirst(null);
    setAddTeacherLast(null);
    setAddTeacherYears(0);
    setAddTeacherGrade0am(false);
    setAddTeacherGrade0pm(false);
    setAddTeacherGrade1(false);
    setAddTeacherGrade2(false);
    setAddTeacherGrade3(false);
    setAddTeacherGrade4(false);
    setAddTeacherGrade5(false);
    setAddTeacherGrade6(false);
    setAddTeacherGrade7(false);
    setAddTeacherGrade8(false);
    setAddTeacherGrade9(false);
    setAddTeacherGrade10(false);
    setAddTeacherGrade11(false);
    setAddTeacherGrade12(false);
    setAddTeacherGrade13(false);
  };

  const resetAddTeacherErrors = () => {
    setAddTeacherFirstError(false);
    setAddTeacherLastError(false);
    setAddTeacherYearsError(false);
  };

  const validateAddTeacher = (teacherToAdd: TeachersNoNulls) => {
    resetAddTeacherErrors();
    let isValid = true;
    if (!teacherToAdd.first) {
      setAddTeacherFirstError(true);
      isValid = false;
    }
    if (!teacherToAdd.last) {
      setAddTeacherLastError(true);
      isValid = false;
    }
    if ((teacherToAdd.years ?? -1) < 0) {
      setAddTeacherYearsError(true);
      isValid = false;
    }
    if (!isValid) {
      console.error("teacherToAdd:", teacherToAdd);
      props.setSnack("Unable to add teacher, check invalid fields", "warning");
    }
    return isValid;
  };

  const processRowAdd = useCallback(
    async (tch: TeachersNoNulls) => {
      setRowsLoading(true);
      try {
        const res = (await addRow(tch)) as any;
        if (Object.keys(res).includes("error")) throw res;
        const teacherAdded = res as TeachersNoNulls[][0];
        setAddDialogOpen(false);
        props.setTeachers(t => [...t, teacherAdded]);
        props.setSnack("Teacher successfully added", "success");
        resetAddTeacher();
      } catch (err: any) {
        console.error(err);
        props.setSnack(
          `${err.error}: ${!err.existing.prefix ? err.existing.first : err.existing.prefix} ${err.existing.last}`,
          "error",
        );
      } finally {
        setRowsLoading(false);
      }
    },
    [props, addRow],
  );

  const processRowsAdds = useCallback(
    async (teachers: TeachersNoNulls[]) => {
      setRowsLoading(true);
      try {
        const res = (await fetchPostTeachers(teachers)) as any;
        if (!res || Object.keys(res).includes("error")) throw res;
        props.setTeachers(t => [...t, ...(res as TeachersNoNulls[])]);
        props.setSnack(`Successfully added ${res.length} Teachers`, "success");
      } catch (err: any) {
        console.error(err);
        props.setSnack(
          err.error.includes('error: duplicate key value violates unique constraint "')
            ? "Failed: duplicates were present"
            : (err.error ?? err.toString()),
          "error",
        );
      } finally {
        setRowsLoading(false);
      }
    },
    [props],
  );

  const processRowRemove = useCallback(
    async (tch: TeachersNoNulls) => {
      setRowsLoading(true);
      try {
        const res = (await removeRow(tch)) as any;
        if (Object.keys(res).includes("error")) throw res;
        props.setTeachers(t => t.filter(t => t.id !== tch.id));
        props.setSnack("Classroom successfully deleted", "success");
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err.error}: ${err.existing.first} ${err.existing.last}`, "error");
      } finally {
        setActionDeleteVisible(false);
        setRowsLoading(false);
      }
    },
    [props, removeRow],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      let diff = false;
      for (const k of Object.keys(newRow)) {
        if (newRow[k] !== oldRow[k]) {
          diff = true;
          break;
        }
      }
      if (!diff) return oldRow;

      setRowsLoading(true);
      try {
        const res = (await mutateRow(newRow as TeachersNoNulls)) as TeachersNoNulls;
        props.setSnack("Teacher successfully updated", "success");
        props.setTeachers(t => t.map(t => (t.id === res.id ? res : t)));
        return res;
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err.error}`, "error");
      } finally {
        setRowsLoading(false);
      }
    },
    [props, mutateRow],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      props.setSnack(error.message, "error");
    },
    [props],
  );

  const performUpload = async (overwrite: boolean) => {
    try {
      if (overwrite) {
        const dropped = await fetchDeleteTeachersAll();
        console.log(dropped);
        props.setTeachers([]);
      }
      await processRowsAdds(uploadPendingRows as TeachersNoNulls[]);
    } catch (err: any) {
      console.error(err);
    } finally {
      setUploadPendingRows(null);
      setOpenUploadOverwrite(false);
    }
  };

  const uploadTeachers = (uploadedRows: Upload[]) => {
    setUploadPendingRows(uploadedRows);
  };

  useEffect(() => {
    if (!uploadPendingRows) return;
    if (props.teachers.length > 0) {
      setOpenUploadOverwrite(true);
    } else {
      performUpload(false);
    }
  }, [uploadPendingRows]);

  const columns: GridColDef[] = [
    {
      field: "actionDelete",
      headerAlign: "center",
      headerName: "Delete",
      width: 70,
      minWidth: 70,
      align: "center",
      sortable: false,
      renderCell: params => (
        <Button startIcon={<DeleteIcon />} onClick={ev => processRowRemove(params.row as TeachersNoNulls)} />
      ),
    },
    {
      field: "prefix",
      type: "singleSelect",
      valueOptions: TEACHER_PREFIXES,
      headerName: "Prefix",
      width: 80,
      minWidth: 80,
      editable: true,
    },
    { field: "first", headerName: "First Name", flex: 2, maxWidth: 200, editable: true },
    { field: "last", headerName: "Last Name", flex: 3, maxWidth: 240, editable: true },
    {
      field: "years",
      type: "number",
      headerAlign: "left",
      headerName: "Years of Service",
      align: "left",
      width: 130,
      minWidth: 130,
      editable: false,
    },
    ...((props.settingsGrades["ECS-AM"]
      ? [{ field: "grade0am", type: "boolean", headerName: "ECS-AM", width: 80, minWidth: 80, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["ECS-PM"]
      ? [{ field: "grade0pm", type: "boolean", headerName: "ECS-PM", width: 80, minWidth: 80, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 1"]
      ? [{ field: "grade1", type: "boolean", headerName: "1", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 2"]
      ? [{ field: "grade2", type: "boolean", headerName: "2", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 3"]
      ? [{ field: "grade3", type: "boolean", headerName: "3", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 4"]
      ? [{ field: "grade4", type: "boolean", headerName: "4", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 5"]
      ? [{ field: "grade5", type: "boolean", headerName: "5", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 6"]
      ? [{ field: "grade6", type: "boolean", headerName: "6", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 7"]
      ? [{ field: "grade7", type: "boolean", headerName: "7", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 8"]
      ? [{ field: "grade8", type: "boolean", headerName: "8", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 9"]
      ? [{ field: "grade9", type: "boolean", headerName: "9", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 10"]
      ? [{ field: "grade10", type: "boolean", headerName: "10", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 11"]
      ? [{ field: "grade11", type: "boolean", headerName: "11", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 12"]
      ? [{ field: "grade12", type: "boolean", headerName: "12", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 13"]
      ? [{ field: "grade13", type: "boolean", headerName: "13", width: 40, minWidth: 40, editable: true }]
      : []) as GridColDef[]),
  ];

  return (
    <>
      <PSDataGrid
        actionDeleteVisible={actionDeleteVisible}
        columns={columns}
        initialSortField="last"
        loading={rowsLoading}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        processRowUpdate={processRowUpdate}
        psType={"Teachers"}
        rows={props.teachers}
        setActionDeleteVisible={setActionDeleteVisible}
        setAddDialogOpen={setAddDialogOpen}
        setSnack={props.setSnack}
        teachers={props.teachers}
        toolbarHeaderRow={
          <Stack direction="row">
            <Box sx={{ width: "40%" }}></Box>
            <Box
              sx={{
                display: "flex",
                width: "60%",
                maxWidth: 700,
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              Grades of Instruction:
            </Box>
          </Stack>
        }
        upload={uploadTeachers}
      ></PSDataGrid>
      <Dialog open={addDialogOpen}>
        <DialogTitle>Add Teacher</DialogTitle>
        <DialogContent>
          <DialogContentText>Asterisks indicate required fields.</DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="add-prefix-label">Prefix</InputLabel>
            <Select
              labelId="add-prefix-label"
              id="add-prefix"
              value={addTeacherPrefix}
              label="Prefix"
              onChange={ev => setAddTeacherPrefix(ev.target.value)}
            >
              {TEACHER_PREFIXES.map(p => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            id="add-first"
            error={addTeacherFirstError}
            fullWidth
            label="First Name"
            margin="dense"
            required
            type="text"
            variant="standard"
            onChange={ev => {
              setAddTeacherFirstError(false);
              setAddTeacherFirst(ev.target.value ?? "");
            }}
          />
          <TextField
            id="add-last"
            error={addTeacherLastError}
            fullWidth
            label="Last Name"
            margin="dense"
            required
            type="text"
            variant="standard"
            onChange={ev => {
              setAddTeacherLastError(false);
              setAddTeacherLast(ev.target.value ?? "");
            }}
          />
          <TextField
            id="add-years"
            error={addTeacherYearsError}
            fullWidth
            label="Years of Service"
            margin="dense"
            required
            type="number"
            value={addTeacherYears}
            variant="standard"
            onChange={ev => {
              setAddTeacherYearsError(false);
              setAddTeacherYears(Number(ev.target.value ?? "0"));
            }}
            onWheel={ev => (ev.target as HTMLInputElement).blur()}
          />
          <FormControl component="fieldset" sx={{ mt: 4 }}>
            <FormLabel component="legend">Grades of Instruction:</FormLabel>
            <FormGroup row>
              {props.settingsGrades["ECS-AM"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade0am(ev.target.checked)} />}
                  label="ECS-AM"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["ECS-PM"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade0pm(ev.target.checked)} />}
                  label="ECS-PM"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 1"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade1(ev.target.checked)} />}
                  label="1"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 2"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade2(ev.target.checked)} />}
                  label="2"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 3"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade3(ev.target.checked)} />}
                  label="3"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 4"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade4(ev.target.checked)} />}
                  label="4"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 5"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade5(ev.target.checked)} />}
                  label="5"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 6"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade6(ev.target.checked)} />}
                  label="6"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 7"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade7(ev.target.checked)} />}
                  label="7"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 8"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade8(ev.target.checked)} />}
                  label="8"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 9"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade9(ev.target.checked)} />}
                  label="9"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 10"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade10(ev.target.checked)} />}
                  label="10"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 11"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade11(ev.target.checked)} />}
                  label="11"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 12"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade12(ev.target.checked)} />}
                  label="12"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
              {props.settingsGrades["Gr 13"] ? (
                <FormControlLabel
                  control={<Checkbox onChange={ev => setAddTeacherGrade13(ev.target.checked)} />}
                  label="13"
                  labelPlacement="start"
                  sx={{ mx: 1.5 }}
                />
              ) : (
                <></>
              )}
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (
                (addTeacherPrefix ?? "N/A") === "N/A" &&
                (addTeacherFirst ?? "") === "" &&
                (addTeacherLast ?? "") === "" &&
                (addTeacherYears ?? -1) === 0
              ) {
                setAddDialogOpen(false);
                resetAddTeacherErrors();
              } else {
                setAddDialogCancelOpen(true);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const teacherToAdd = {
                prefix: addTeacherPrefix,
                first: addTeacherFirst,
                last: addTeacherLast,
                years: addTeacherYears,
                grade0am: addTeacherGrade0am,
                grade0pm: addTeacherGrade0pm,
                grade1: addTeacherGrade1,
                grade2: addTeacherGrade2,
                grade3: addTeacherGrade3,
                grade4: addTeacherGrade4,
                grade5: addTeacherGrade5,
                grade6: addTeacherGrade6,
                grade7: addTeacherGrade7,
                grade8: addTeacherGrade8,
                grade9: addTeacherGrade9,
                grade10: addTeacherGrade10,
                grade11: addTeacherGrade11,
                grade12: addTeacherGrade12,
                grade13: addTeacherGrade13,
              } as TeachersNoNulls;
              if (!validateAddTeacher(teacherToAdd)) return;
              processRowAdd(teacherToAdd);
            }}
            variant="contained"
          >
            Add Teacher
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addDialogCancelOpen} onClose={() => setAddDialogCancelOpen(false)}>
        <DialogContent>
          <DialogTitle>Cancel anyway?</DialogTitle>
          <DialogContentText>If you cancel now, you'll lose all current data entered.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogCancelOpen(false)}>Continue editing</Button>
          <Button
            onClick={() => {
              setAddDialogCancelOpen(false);
              setAddDialogOpen(false);
              resetAddTeacher();
              resetAddTeacherErrors();
            }}
            variant="outlined"
          >
            Discard & Close
          </Button>
        </DialogActions>
      </Dialog>
      <UploadOverwriteDialog
        open={openUploadOverwrite}
        upload={performUpload}
        uploadCancel={() => {
          setUploadPendingRows(null);
          setOpenUploadOverwrite(false);
        }}
      />
    </>
  );
};
